import { CSSProperties } from 'react';
import useWidthBreakpoint from '../../../../../../hooks/useWidthBreakpoint';
import {
  SummaryTx,
  txColor,
  txDirection,
  txDirectionColor,
  txDisplayId,
  txIsCore,
  txIsCustom,
  txLabel,
  txProfitLabel,
} from '../../txHelpers';
import { profitColor, tokens } from '../../tokens';
import { pick } from '../../responsive';
import BalancePill from './BalancePill';

interface Props {
  tx: SummaryTx;
  /** When set, the header becomes a clickable collapse target (expanded views). */
  onHeaderClick?: () => void;
}

/**
 * The transaction summary panel — #id header, order price, realised profit and
 * balance split. The closed row, the expanded row and the MRC deep-dive row all
 * render this, so the values are pixel-identical and never shift between views.
 */
const SummaryPanel = ({ tx, onHeaderClick }: Props) => {
  const { breakpoint } = useWidthBreakpoint();
  const accentColor = txColor(tx.type);
  const profitLabel = txProfitLabel(tx);
  // A custom transaction's headline is a moved amount, not a gain — colouring
  // it green/red by sign would read as profit it never made.
  const isCustom = txIsCustom(tx.type);
  // Header / divider / values spacing — fixed here rather than inherited from
  // the parent rail's gap, so it is identical in every view.
  const gap = pick(breakpoint, { sm: 12, md: 16, lg: 16 });

  const valueLabel: CSSProperties = {
    fontSize: 11,
    color: tokens.textSecondary,
    fontFamily: tokens.fontMono,
    letterSpacing: '0.15em',
    fontWeight: 600,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <div
        className={onHeaderClick ? 'nc-row-hover' : undefined}
        onClick={onHeaderClick}
        style={{
          cursor: onHeaderClick ? 'pointer' : undefined,
          padding: '8px 12px',
          margin: '-8px -12px',
          borderRadius: 4,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 22,
              fontFamily: tokens.fontMono,
              color: accentColor,
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            #{txDisplayId(tx.id)}
          </span>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: txIsCore(tx.type) ? tokens.textPrimary : accentColor,
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              {txLabel(tx.type)}
            </span>
            <span
              style={{
                fontSize: 10,
                color: txDirectionColor(tx),
                fontFamily: tokens.fontMono,
                letterSpacing: '0.1em',
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              {txDirection(tx)}
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: 12,
            color: tokens.textTertiary,
            fontFamily: tokens.fontMono,
            marginTop: 4,
            letterSpacing: '0.05em',
          }}
        >
          {tx.date}
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={valueLabel}>ORDER PRICE</span>
          <span
            style={{
              fontSize: 30,
              fontFamily: tokens.fontDisplay,
              color: tokens.textPrimary,
              lineHeight: 1,
            }}
          >
            {tx.price}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={valueLabel}>{profitLabel}</span>
          <span
            style={{
              fontSize: 36,
              fontFamily: tokens.fontDisplay,
              color: isCustom
                ? tokens.textPrimary
                : tx.profit === '0%'
                ? tokens.textMuted
                : profitColor(tx.profit),
              lineHeight: 1,
            }}
          >
            {tx.profit}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={valueLabel}>BALANCE DISTRIBUTION</span>
          <BalancePill stableFraction={tx.stableShare} width={90} />
        </div>
      </div>
    </div>
  );
};

export default SummaryPanel;
